import React, { createContext, useEffect, useState, useContext, ReactNode, useCallback } from 'react';

interface LocationContextValue {
  location: Location | null;
  isPermittionDenied: boolean;
  askPermittionForLocation(): void;
  positionWasRequested: boolean;
}

interface Location {
  latitude: number;
  longitude: number;
}

const LocationContext = createContext<LocationContextValue>({} as LocationContextValue);

interface LocationProviderProps {
  children: ReactNode;
}

const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [location, setLocation] = useState<Location | null>(null);
  const [isPermittionDenied, setIsPermittionDenied] = useState(false);
  const [positionWasRequested, setPositionWasRequested] = useState(false);

  const askPermittionForLocation = useCallback(() => {
    setIsPermittionDenied(false);
    setPositionWasRequested(true);

    navigator.geolocation.getCurrentPosition(
      success => {
        setLocation({
          latitude: success.coords.latitude,
          longitude: success.coords.longitude,
        });
      },
      error => {
        console.error(error);
        setIsPermittionDenied(true);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 5000,
      }
    );
  }, []);

  useEffect(() => {
    askPermittionForLocation();
  }, [askPermittionForLocation]);

  return (
    <LocationContext.Provider value={{ location, isPermittionDenied, askPermittionForLocation, positionWasRequested }}>
      {children}
    </LocationContext.Provider>
  );
};

export function useLocation(): LocationContextValue {
  const context = useContext(LocationContext);
  return context;
}

export default LocationProvider;
